// Session detail drawer. Opens when a row is clicked in SessionsTab.
// Fetches /admin/sessions/{id}/events and shows the per-query timeline
// (matches the side-drawer the legacy admin.js rendered).

import { useEffect, useState } from "react";
import type { OrgScope, SessionQueryEvent, SessionRow } from "../../api/admin";
import { adminApi } from "../../api/admin";
import { ErrorBlock, LoadingBlock, formatDateTime, formatNumber } from "./_shared";

interface Props {
  bearer: string;
  scope: OrgScope;
  session: SessionRow | null;
  onClose: () => void;
}

export function SessionDrawer({ bearer, scope, session, onClose }: Props) {
  const [events, setEvents] = useState<SessionQueryEvent[]>([]);
  const [meta, setMeta] = useState<{ user_email?: string | null; organization?: string | null; source?: string | null }>({});
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    adminApi
      .sessionEvents(bearer, session.session_id, scope)
      .then((d) => {
        if (cancelled) return;
        setEvents(d.events || []);
        setMeta({ user_email: d.user_email, organization: d.organization, source: d.source });
        setLoading(false);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(e);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [bearer, scope, session]);

  if (!session) return null;

  const user = meta.user_email || session.user_email || "";
  const org = meta.organization || session.organization || "";
  const source = meta.source || session.source || "";
  const queryCount = events.length;
  // Sessions originating from web2 chat all share one thread; pick the
  // first event that carries a thread_id and use it for the "View
  // Thread" header link. mcp / sdk / slack sessions have no thread and
  // get no link.
  const threadId = events.find((e) => e.thread_id != null)?.thread_id ?? null;

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/30"
        onClick={onClose}
        role="presentation"
      />
      <aside className="fixed right-0 top-0 z-50 h-full w-full max-w-3xl bg-white shadow-2xl border-l border-neutral-200 overflow-y-auto">
        <header className="sticky top-0 bg-white border-b border-neutral-200 px-5 py-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-neutral-900 truncate">
              Session {session.session_id.slice(0, 12)}…
            </h2>
            <p className="text-xs text-neutral-500 mt-0.5 truncate">
              {user}
              {org && <> · {org}</>}
              {source && <> · {source}</>}
              {session.started_at && <> · {formatDateTime(session.started_at)}</>}
              <> · {queryCount} {queryCount === 1 ? "query" : "queries"}</>
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {threadId != null && (
              <a
                href={`/?thread=${threadId}`}
                target="_blank"
                rel="noreferrer"
                className="rounded-md border border-line text-brand px-2.5 h-7 inline-flex items-center text-xs font-medium hover:bg-chip"
              >
                View Thread →
              </a>
            )}
            <button
              type="button"
              onClick={onClose}
              className="text-neutral-500 hover:text-neutral-900 px-2 py-1 rounded"
              aria-label="Close"
            >
              ✕
            </button>
          </div>
        </header>

        <div className="p-5 space-y-3">
          {loading && <LoadingBlock />}
          {error && <ErrorBlock error={error} />}
          {!loading && !error && events.length === 0 && (
            <div className="text-center text-neutral-500 py-12 text-sm">No events in this session</div>
          )}
          {!loading && !error && events.map((e, i) => <EventCard key={`${e.query_id}-${i}`} event={e} />)}
        </div>
      </aside>
    </>
  );
}

function EventCard({ event }: { event: SessionQueryEvent }) {
  const statusClass =
    event.status === "completed"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : event.status === "error"
        ? "bg-red-50 text-red-700 border-red-200"
        : "bg-neutral-50 text-neutral-700 border-neutral-200";

  const viewHref =
    event.source === "web" && event.thread_id
      ? `/?thread=${event.thread_id}${event.message_id ? `&message=${event.message_id}` : ""}`
      : null;

  return (
    <article className="border border-neutral-200 rounded-md p-3 text-xs space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-neutral-500">{formatDateTime(event.timestamp)}</span>
        <div className="flex items-center gap-2">
          {event.status && (
            <span className={`inline-block px-1.5 py-0.5 border rounded text-[10px] uppercase tracking-wide ${statusClass}`}>
              {event.status}
            </span>
          )}
          {event.execution_time_ms != null && (
            <span className="text-neutral-500">{formatNumber(Math.round(event.execution_time_ms))} ms</span>
          )}
          {viewHref && (
            <a
              href={viewHref}
              target="_blank"
              rel="noreferrer"
              className="text-brand hover:underline"
            >
              View Response
            </a>
          )}
        </div>
      </div>
      <div className="text-neutral-900 font-medium break-words">{event.raw_query}</div>
      {event.agent_sub_query && (
        <div className="text-neutral-600 italic break-words">↳ {event.agent_sub_query}</div>
      )}
      {event.datasource_name && (
        <div className="text-neutral-500">Datasource: {event.datasource_name}</div>
      )}
      {event.sql_generated && (
        <pre className="bg-neutral-50 border border-neutral-200 rounded p-2 text-[11px] overflow-x-auto whitespace-pre-wrap break-words">
          {event.sql_generated}
        </pre>
      )}
      {event.error_message && (
        <div className="text-red-700 bg-red-50 border border-red-200 rounded p-2 whitespace-pre-wrap break-words">
          {event.error_message}
        </div>
      )}
    </article>
  );
}
